import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useStripe } from '@stripe/react-stripe-js'

import { PUBSUB_GROUP_EVENT, ROUTES } from '@common'
import { Loader } from '@components'
import { StripeProvider } from '@services'
import { storageHelpers } from '@utils'
import { PubSubService } from '@services'
import { useCashRegister } from '@hooks'

const PAYMENT_STATUS = {
    CHECKING: 'checking',
    SUCCESS: 'success',
    FAILED: 'failed',
    ERROR: 'error',
}

const CheckPaymentPageInner = () => {
    const { orderId, clientSecret } = useParams()
    const stripe = useStripe()
    const navigate = useNavigate()
    const [countdown, setCountdown] = useState(0)
    const [status, setStatus] = useState(PAYMENT_STATUS.CHECKING)
    const [isLoading, setIsLoading] = useState(true)
    const { joinedGroupName } = useCashRegister()

    const handleNavigate = useCallback(() => {
        const paymentOrigin = storageHelpers.getPaymentOrigin()
        storageHelpers.clearPaymentOrigin()

        if (paymentOrigin === 'checkout-assistant') {
            PubSubService.sendToGroup(joinedGroupName, { type: PUBSUB_GROUP_EVENT.PAYMENT_PAYNOW_SUCCESS })
            navigate(ROUTES.CheckoutAssistant.root)
        } else {
            navigate(ROUTES.Order.complete.replace(':orderId', orderId), { replace: true, state: { isPaid: true } })
        }
    }, [navigate, orderId])

    useEffect(() => {
        if (!stripe || !clientSecret) {
            return
        }

        let mounted = true

        const checkStatus = async () => {
            try {
                setIsLoading(true)

                const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret)
                if (mounted) {
                    if (paymentIntent && paymentIntent.status === 'succeeded') {
                        setStatus(PAYMENT_STATUS.SUCCESS)
                    } else {
                        setStatus(PAYMENT_STATUS.FAILED)
                    }
                }
            } catch (error) {
                if (mounted) {
                    setStatus(PAYMENT_STATUS.ERROR)
                }
            } finally {
                setIsLoading(false)
            }
        }

        checkStatus()
        return () => {
            mounted = false
        }
    }, [stripe, clientSecret])

    useEffect(() => {
        if (status !== PAYMENT_STATUS.SUCCESS) {
            return
        }

        if (countdown <= 0) {
            handleNavigate()
            return
        }

        const timer = setInterval(() => {
            setCountdown(prev => prev - 1)
        }, 1000)

        return () => clearInterval(timer)
    }, [status, countdown, handleNavigate])

    const renderContent = () => {
        switch (status) {
            case PAYMENT_STATUS.CHECKING:
                return (
                    <>
                        <h2 className="mb-4">Checking payment status...</h2>
                    </>
                )
            case PAYMENT_STATUS.SUCCESS:
                return (
                    <>
                        <h2 className="mb-4 text-primary">Payment Successful! 🎉</h2>
                        <p className="mb-4">Redirecting to order details in {countdown} seconds...</p>
                    </>
                )
            case PAYMENT_STATUS.FAILED:
                return (
                    <>
                        <h2 className="mb-4 text-danger">Payment failed! Please try again.</h2>
                        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                            Back to Payment
                        </button>
                    </>
                )
            case PAYMENT_STATUS.ERROR:
                return (
                    <>
                        <h2 className="mb-4 text-danger">Something went wrong. Please try again.</h2>
                        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                            Back to Payment
                        </button>
                    </>
                )
            default:
                return null
        }
    }

    return (
        <>
            {isLoading && <Loader />}
            <div className="text-center mt-5">{renderContent()}</div>
        </>
    )
}

const CheckPaymentPage = () => {
    return (
        <StripeProvider>
            <CheckPaymentPageInner />
        </StripeProvider>
    )
}

export default CheckPaymentPage
